import { useMemo } from 'react'
import clsx from 'clsx'

interface ArtifactMeterProps {
  label: string
  value: number
  threshold?: number
  min?: number
  max?: number
  unit?: string
  inverted?: boolean
  description?: string
  severity?: 'good' | 'moderate' | 'high' | 'critical'
}

export function ArtifactMeter({
  label,
  value,
  threshold = 0.3,
  min = 0,
  max = 1,
  unit,
  inverted = false,
  description,
  severity,
}: ArtifactMeterProps) {
  const { fillPercent, colorClass, displayValue, glowClass } = useMemo(() => {
    const percent = Math.max(0, Math.min(100, ((value - min) / (max - min)) * 100))

    let meterColorClass: string
    let meterGlowClass: string

    if (severity) {
      switch (severity) {
        case 'good':
          meterColorClass = 'good'
          meterGlowClass = 'shadow-[0_0_16px_hsl(var(--forge-success)/0.4)]'
          break
        case 'moderate':
          meterColorClass = 'moderate'
          meterGlowClass = 'shadow-[0_0_16px_hsl(var(--forge-warning)/0.4)]'
          break
        case 'high':
          meterColorClass = 'high'
          meterGlowClass = 'shadow-[0_0_16px_hsl(var(--forge-copper)/0.4)]'
          break
        case 'critical':
          meterColorClass = 'critical'
          meterGlowClass = 'shadow-[0_0_16px_hsl(var(--forge-danger)/0.4)]'
          break
      }
    } else {
      const normalizedThreshold = ((threshold - min) / (max - min)) * 100
      if (inverted) {
        if (percent > normalizedThreshold * 2) {
          meterColorClass = 'critical'
          meterGlowClass = 'shadow-[0_0_16px_hsl(var(--forge-danger)/0.4)]'
        } else if (percent > normalizedThreshold) {
          meterColorClass = 'moderate'
          meterGlowClass = 'shadow-[0_0_16px_hsl(var(--forge-warning)/0.4)]'
        } else {
          meterColorClass = 'good'
          meterGlowClass = 'shadow-[0_0_16px_hsl(var(--forge-success)/0.4)]'
        }
      } else {
        if (percent < normalizedThreshold) {
          meterColorClass = 'critical'
          meterGlowClass = 'shadow-[0_0_16px_hsl(var(--forge-danger)/0.4)]'
        } else if (percent < normalizedThreshold * 1.5) {
          meterColorClass = 'moderate'
          meterGlowClass = 'shadow-[0_0_16px_hsl(var(--forge-warning)/0.4)]'
        } else {
          meterColorClass = 'good'
          meterGlowClass = 'shadow-[0_0_16px_hsl(var(--forge-success)/0.4)]'
        }
      }
    }

    let display: string
    if (max === 1 && min === 0 && !unit) {
      display = (value * 10).toFixed(1)
    } else if (unit) {
      display = `${value.toFixed(1)}${unit}`
    } else {
      display = value.toFixed(1)
    }

    return {
      fillPercent: percent,
      colorClass: meterColorClass,
      displayValue: display,
      glowClass: meterGlowClass,
    }
  }, [value, threshold, min, max, unit, inverted, severity])

  const severityTextColor = useMemo(() => {
    switch (severity) {
      case 'good':
        return 'text-forge-success'
      case 'moderate':
        return 'text-forge-warning'
      case 'high':
        return 'text-forge-copper'
      case 'critical':
        return 'text-forge-danger'
      default:
        return 'text-white'
    }
  }, [severity])

  const severityLabel = useMemo(() => {
    switch (severity) {
      case 'good':
        return 'Good'
      case 'moderate':
        return 'Fair'
      case 'high':
        return 'High'
      case 'critical':
        return 'Critical'
      default:
        return null
    }
  }, [severity])

  return (
    <div className="p-4 bg-forge-elevated rounded-xl border border-forge">
      {/* Header row */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-white">{label}</span>
        <div className="flex items-center gap-2">
          {severityLabel && (
            <span className={clsx('text-xs font-medium uppercase tracking-wide', severityTextColor)}>
              {severityLabel}
            </span>
          )}
          <span className={clsx('text-sm font-mono font-medium', severityTextColor)}>
            {displayValue}
          </span>
        </div>
      </div>

      {/* Meter bar */}
      <div className="forge-meter">
        <div
          className={clsx('forge-meter-fill', colorClass, glowClass)}
          style={{ width: `${fillPercent}%` }}
        />

        {/* Threshold line */}
        {threshold !== undefined && !severity && (
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-white/40"
            style={{ left: `${((threshold - min) / (max - min)) * 100}%` }}
          />
        )}

        {/* Tick marks */}
        <div className="absolute inset-0 flex justify-between px-1 items-center pointer-events-none">
          {[0, 25, 50, 75, 100].map((tick) => (
            <div
              key={tick}
              className="w-px h-1.5 bg-white/20"
            />
          ))}
        </div>
      </div>

      {/* Description */}
      {description && (
        <p className="mt-2.5 text-xs text-forge-muted leading-relaxed">{description}</p>
      )}
    </div>
  )
}
